/**
 * Reminder Hooks - T128
 *
 * TanStack Query hooks for payment reminder operations
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { useActiveTenantId } from './useTenantContext';
import type { TenantKey } from '../services/api/keys';
import type { CreateReminderInput, UpdateReminderInput, ReminderListQuery } from '@armard/shared';

// ─────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────

export interface PaymentReminder {
  id: string;
  invoiceId: string;
  reminderLevel: number;
  reminderDate: string;
  dueDate: string;
  reminderFee: string;
  interestRate: string | null;
  interestAmount: string;
  totalDue: string;
  notes: string | null;
  pdfGeneratedAt: string | null;
  pdfStoragePath: string | null;
  createdAt: string;
  updatedAt: string;
  invoice?: {
    id: string;
    invoiceNumber: string;
    customerId: string;
    grossTotal: string;
    dueDate: string;
    status: string;
    customer?: {
      id: string;
      companyName: string;
    };
  };
}

export interface ReminderListResult {
  reminders: PaymentReminder[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// ─────────────────────────────────────────────────────────────
// QUERY KEYS
// ─────────────────────────────────────────────────────────────

export const reminderKeys = {
  all: (t: TenantKey) => ['reminders', t] as const,
  lists: (t: TenantKey) => [...reminderKeys.all(t), 'list'] as const,
  list: (t: TenantKey, query: Partial<ReminderListQuery>) =>
    [...reminderKeys.lists(t), query] as const,
  forInvoice: (t: TenantKey, invoiceId: string) =>
    [...reminderKeys.all(t), 'invoice', invoiceId] as const,
  details: (t: TenantKey) => [...reminderKeys.all(t), 'detail'] as const,
  detail: (t: TenantKey, id: string) => [...reminderKeys.details(t), id] as const,
};

// ─────────────────────────────────────────────────────────────
// REMINDER HOOKS
// ─────────────────────────────────────────────────────────────

/**
 * Hook to list reminders with pagination
 */
export function useReminderList(query: Partial<ReminderListQuery> = {}) {
  const tenantId = useActiveTenantId();
  const fullQuery = {
    page: query.page ?? 1,
    limit: query.limit ?? 40,
    ...query,
  };

  return useQuery({
    queryKey: reminderKeys.list(tenantId, fullQuery),
    enabled: tenantId !== null,
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', String(fullQuery.page));
      params.set('limit', String(fullQuery.limit));
      if (fullQuery.invoiceId) params.set('invoiceId', fullQuery.invoiceId);
      if (fullQuery.level) params.set('level', String(fullQuery.level));

      return api.get<ReminderListResult>(`/reminders?${params.toString()}`);
    },
  });
}

/**
 * Hook to get reminders for a specific invoice
 */
export function useRemindersForInvoice(invoiceId: string) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: reminderKeys.forInvoice(tenantId, invoiceId),
    queryFn: () => api.get<PaymentReminder[]>(`/reminders/invoice/${invoiceId}`),
    enabled: !!invoiceId && tenantId !== null,
  });
}

/**
 * Hook to get a single reminder by ID
 */
export function useReminder(id: string) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: reminderKeys.detail(tenantId, id),
    queryFn: () => api.get<PaymentReminder>(`/reminders/${id}`),
    enabled: !!id && tenantId !== null,
  });
}

/**
 * Hook to create a reminder
 */
export function useCreateReminder() {
  const queryClient = useQueryClient();
  const tenantId = useActiveTenantId();

  return useMutation({
    mutationFn: (input: CreateReminderInput) => api.post<PaymentReminder>('/reminders', input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: reminderKeys.lists(tenantId) });
      queryClient.invalidateQueries({ queryKey: reminderKeys.forInvoice(tenantId, data.invoiceId) });
      // Also invalidate invoice list since status may have changed (cross-resource,
      // bare prefix matches every tenant's invoice caches).
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

/**
 * Hook to update a reminder
 */
export function useUpdateReminder() {
  const queryClient = useQueryClient();
  const tenantId = useActiveTenantId();

  return useMutation({
    mutationFn: ({ id, ...input }: UpdateReminderInput & { id: string }) =>
      api.patch<PaymentReminder>(`/reminders/${id}`, input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: reminderKeys.lists(tenantId) });
      queryClient.invalidateQueries({ queryKey: reminderKeys.forInvoice(tenantId, data.invoiceId) });
      queryClient.setQueryData(reminderKeys.detail(tenantId, data.id), data);
    },
  });
}

/**
 * Hook to delete a reminder
 */
export function useDeleteReminder() {
  const queryClient = useQueryClient();
  const tenantId = useActiveTenantId();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/reminders/${id}`),
    onSuccess: () => {
      // We don't know the invoiceId here, so invalidate the whole
      // reminders subtree for the active tenant (catches forInvoice too).
      queryClient.invalidateQueries({ queryKey: reminderKeys.all(tenantId) });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// PDF DOWNLOAD
// ─────────────────────────────────────────────────────────────

/**
 * Download reminder PDF
 */
export function downloadReminderPdf(reminderId: string): void {
  window.open(`/api/v1/reminders/${reminderId}/pdf`, '_blank');
}

/**
 * Preview reminder PDF
 */
export function previewReminderPdf(reminderId: string): void {
  window.open(`/api/v1/reminders/${reminderId}/pdf/preview`, '_blank');
}

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────

export const reminderLevelLabels: Record<number, string> = {
  1: '1. Mahnung',
  2: '2. Mahnung',
  3: 'Letzte Mahnung',
};

export const reminderLevelColors: Record<number, string> = {
  1: 'bg-yellow-500/20 text-yellow-400',
  2: 'bg-orange-500/20 text-orange-400',
  3: 'bg-red-500/20 text-red-400',
};
